import type { ReactNode } from "react";

export type NativeAutofillOption = {
  /** What we store (`"CA"`). */
  value: string;
  /** What the BROWSER matches against ("California") — not necessarily the visible label. */
  label: string;
};

type NativeAutofillMirrorProps = {
  /** WHATWG autofill token, e.g. "address-level1", "country-name". */
  autoComplete: string;
  /** Form field name the browser groups by. */
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: NativeAutofillOption[];
  disabled?: boolean;
  /** The visible (div-based) control. */
  children: ReactNode;
};

/**
 * Makes a div-based combobox reachable by browser address autofill.
 *
 * Autofill only fills NATIVE form controls, so we overlay a real `<select>` on
 * the visible control. Invariants — all four are load-bearing:
 *  1. FULL SIZE (`absolute inset-0 h-full w-full`), never `sr-only` / 1px:
 *     Chrome skips fields below a minimum rendered size.
 *  2. `opacity-0` + `pointer-events-none`: invisible, and every click still
 *     lands on the visible control underneath.
 *  3. `tabIndex={-1}` + `aria-hidden`: never in the tab order, never announced.
 *  4. A leading empty `<option value="" />` so an unset value is representable.
 */
export function NativeAutofillMirror({
  autoComplete,
  name,
  value,
  onChange,
  options,
  disabled,
  children,
}: NativeAutofillMirrorProps) {
  return (
    <div className="relative w-full">
      <select
        name={name}
        autoComplete={autoComplete}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        tabIndex={-1}
        aria-hidden="true"
        className="absolute inset-0 h-full w-full opacity-0 pointer-events-none"
      >
        <option value="" />
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {children}
    </div>
  );
}
